import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mail, RefreshCw, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const CAT_LABELS = {
  otp: "Code d'inscription",
  otp_resend: "Code renvoyé",
  admin_notify: "Notification admin",
  invoice: "Facture",
  welcome: "Bienvenue",
  alert: "Alerte",
  generic: "Autre",
};
const FILTERS = [
  { v: "", label: "Tous" },
  { v: "sent", label: "Envoyés" },
  { v: "failed", label: "Échecs" },
];
const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function AdminEmailLog() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0 });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/email-logs?limit=200${status ? `&status=${status}` : ""}`)
      .then((r) => { setLogs(r.data.logs || []); setStats(r.data.stats || { total: 0, sent: 0, failed: 0 }); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-email-log-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#C6A87C]" />
          <h2 className="font-display text-xl text-[#0F172A]">Journal des envois e-mail</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500" data-testid="admin-email-stats">
            <b className="text-emerald-600">{stats.sent}</b> envoyés · <b className="text-red-600">{stats.failed}</b> échecs · {stats.total} total
          </span>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="h-8 rounded-full" data-testid="admin-email-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      <div className="mb-3 flex gap-2">
        {FILTERS.map((f) => (
          <button key={f.v || "all"} onClick={() => setStatus(f.v)} data-testid={`admin-email-filter-${f.v || "all"}`}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${status === f.v ? "bg-[#0F172A] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
              <th className="py-2 pr-3">Date</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Destinataire</th>
              <th className="py-2 pr-3">Statut</th>
              <th className="py-2 pr-3">Fournisseur</th>
              <th className="py-2 pr-3">Réf. / Motif</th>
            </tr>
          </thead>
          <tbody data-testid="admin-email-log-body">
            {logs.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-slate-400" data-testid="admin-email-empty">Aucun envoi pour le moment.</td></tr>
            ) : logs.map((l) => (
              <tr key={l.id} className="border-b border-slate-100 last:border-0" data-testid={`admin-email-row-${l.id}`}>
                <td className="py-2 pr-3 text-slate-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                <td className="py-2 pr-3 text-slate-700">{CAT_LABELS[l.category] || l.category}</td>
                <td className="py-2 pr-3 text-slate-700">{l.to}</td>
                <td className="py-2 pr-3">
                  {l.status === "sent" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid={`admin-email-status-${l.id}`}><CheckCircle2 className="h-3 w-3" /> Envoyé</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700" data-testid={`admin-email-status-${l.id}`}><XCircle className="h-3 w-3" /> Échec</span>
                  )}
                </td>
                <td className="py-2 pr-3 text-slate-500">{l.provider}</td>
                <td className="py-2 pr-3 max-w-[220px] truncate text-xs text-slate-400" title={l.reason || l.provider_id || ""}>{l.status === "sent" ? (l.provider_id || "—") : (l.reason || "—")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
